import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { api } from "@/lib/api";
import { useOptionalPermission } from "@/lib/permissions";

import type { MarketCommodity } from "@/types/market-intelligence";
import type { ZoneRef } from "@/types/market-zone-prefs";

const RECENTS_KEY = ["market", "me", "recents"] as const;
/** Exported so nav code can read the same cache without a hook — see
 *  `region-nav.ts`, whose row builders are plain functions. */
export const MARKET_PINS_KEY = ["market", "me", "pins"] as const;
const PINS_KEY = MARKET_PINS_KEY;

/**
 * Per-user Market Intelligence recents and pins.
 *
 * Mirrors `useRecents` / `useRecordVisit` deliberately — same fire-and-forget
 * stamping, same feature gate on the write, same "a miss must never break the
 * page" discipline. The two systems stay separate because a pricing point has
 * no uuid to key on and a pin is not a visit; see
 * `pilot-domain/src/market/zone_prefs.rs` for the full reasoning.
 *
 * A point is identified by `(commodity, code)` everywhere here, never by code
 * alone: the power and gas code namespaces are independent.
 */

/** The caller's recently opened pricing points, newest first. */
export function useMarketZoneRecents(limit?: number, enabled = true) {
  return useQuery({
    // `limit` is part of the key so a 4-row widget and a longer list cannot
    // clobber each other's cache entry — the same trap `useRecents` documents.
    queryKey: [...RECENTS_KEY, limit ?? null],
    queryFn: () =>
      api.get<ZoneRef[]>(
        `/api/market/me/recents${limit != null ? `?limit=${limit}` : ""}`,
      ),
    enabled,
  });
}

/** The caller's pinned pricing points, in their own order. */
export function useMarketZonePins(enabled = true) {
  return useQuery({
    queryKey: PINS_KEY,
    queryFn: () => api.get<ZoneRef[]>("/api/market/me/pins"),
    enabled,
  });
}

/**
 * Stamps "the signed-in user opened this pricing point".
 *
 * Fire-and-forget: the page never waits on it and a failure is swallowed,
 * because a recents miss must never break the workspace the user actually
 * asked for.
 *
 * Pass `undefined` while the hub is still resolving — the effect no-ops until
 * both halves of the identity are real.
 */
export function useMarketZoneVisit(
  commodity: MarketCommodity | undefined,
  code: string | undefined,
) {
  const queryClient = useQueryClient();
  // Only record when the widget that consumes this can be reached. Both the
  // dashboard panel and the module itself need `market_intelligence.access`,
  // so without it every hub page view would POST a visit — an upsert plus a
  // trim DELETE — for a reader who cannot see the result. The hook stays
  // unconditionally CALLED; the permission only no-ops the effect, so there is
  // no conditional-hook problem.
  const enabled = useOptionalPermission("market_intelligence.access");

  useEffect(() => {
    if (!enabled || !commodity || !code) return;
    let cancelled = false;

    void api
      .post(
        `/api/market/me/recents/${encodeURIComponent(commodity)}/${encodeURIComponent(code)}`,
      )
      .then(() => {
        // Refresh the widget so returning to a dashboard in the same session
        // reflects this visit. The cancelled-guard keeps an unmounted page
        // from invalidating after the user has moved on.
        if (!cancelled) {
          void queryClient.invalidateQueries({ queryKey: RECENTS_KEY });
        }
      })
      .catch(() => {
        // Intentionally silent — see the doc comment.
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, commodity, code, queryClient]);
}

/**
 * Pin and unpin, as one hook so a toggle has a single `isPending`.
 *
 * Errors are NOT swallowed here, unlike the visit stamp: pinning is a
 * deliberate action, and silently doing nothing when the cap is hit would
 * leave the user clicking a control that never responds. The caller surfaces
 * the server's message (`MAX_PINS` is named in it).
 */
export function useMarketZonePinToggle() {
  const queryClient = useQueryClient();
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: PINS_KEY });

  const pin = useMutation({
    mutationFn: (zone: { commodity: string; code: string }) =>
      api.post("/api/market/me/pins", zone),
    onSuccess: invalidate,
  });

  const unpin = useMutation({
    mutationFn: (zone: { commodity: string; code: string }) =>
      api.del(
        `/api/market/me/pins/${encodeURIComponent(zone.commodity)}/${encodeURIComponent(zone.code)}`,
      ),
    onSuccess: invalidate,
  });

  return {
    pin,
    unpin,
    isPending: pin.isPending || unpin.isPending,
  };
}

/** Rewrites the pin order from the arrangement the client just rendered. */
export function useReorderMarketZonePins() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pins: Array<{ commodity: string; code: string }>) =>
      api.put("/api/market/me/pins", { pins }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: PINS_KEY }),
  });
}
